class WineFinder extends HTMLElement {
  constructor() {
      super();
      this.changedByUser = false; // Track if user has changed a filter
      this.hasReloaded = false;
  }

  connectedCallback() {
      this.loadInitialFilters();
      this.setupButton();
      this.setupButtonStateCheck();
  }

  async loadInitialFilters() {
      const initialFilters = {};
      const fetchUrl = this.buildFetchUrl(initialFilters);
      if (!fetchUrl) return;

      try {
          const response = await fetch(fetchUrl);
          if (!response.ok) {
              throw new Error(`Failed to load filters: ${response.statusText}`);
          }

          const text = await response.text();

          const doc = new DOMParser().parseFromString(text, "text/html");
          this.populateAllFilters(doc);
      } catch (error) {
          console.error("Error loading filters:", error);
      }
  }

  async loadFilteredOptions(changedElement, nextElement) {
      if (!this.changedByUser) {
          return;
      }

      console.log("[WineFinder] loadFilteredOptions: changedElement=", changedElement?.getAttribute?.("data-filter"), "nextElement=", nextElement?.getAttribute?.("data-filter") || nextElement);

      const filters = this.collectSelectedFilters(changedElement);
      console.log("[WineFinder] collected filters:", JSON.stringify(filters, null, 2));

      const fetchUrl = this.buildFetchUrl(filters);
      console.log("[WineFinder] fetchUrl:", fetchUrl);
      if (!fetchUrl) return;

      try {
          const response = await fetch(fetchUrl);
          if (!response.ok) {
              throw new Error(`Failed to load filtered options: ${response.statusText}`);
          }

          const text = await response.text();

          const doc = new DOMParser().parseFromString(text, "text/html");
          this.populateSubsequentFilters(doc, changedElement);
          if (nextElement) {
              this.loadFilteredOptions(nextElement, false);
          }
          this.checkButtonState();
      } catch (error) {
          console.error("Error loading filtered options:", error);
      }
  }

  async reloadFilters() {
      if (this.hasReloaded) return;
      await this.getTemplateId();
      const filters = this.collectSelectedFilters();
      const fetchUrl = this.buildFetchUrl(filters);
      console.log(fetchUrl);  
      if (!fetchUrl) return;

      try {
          const response = await fetch(fetchUrl);
          if (!response.ok) {
              throw new Error(`Failed to load products: ${response.statusText}`);
          }

          const text = await response.text();

          const doc = new DOMParser().parseFromString(text, "text/html");
          this.populateAllFilters(doc);
      } catch (error) {
          console.error("Error loading products:", error);
      }
  }

  async loadProducts() {
      await this.getTemplateId();
      const filters = this.collectSelectedFilters();
      const fetchUrl = this.buildFetchUrl(filters);
      console.log(fetchUrl);  
      if (!fetchUrl) return;

      try {
          const response = await fetch(fetchUrl);
          if (!response.ok) {
              throw new Error(`Failed to load products: ${response.statusText}`);
          }

          const text = await response.text();

          const doc = new DOMParser().parseFromString(text, "text/html");
          this.updateProductGrid(doc);
      } catch (error) {
          console.error("Error loading products:", error);
      }
  }

  async getTemplateId() {
      const collectionUrl = this.dataset.url;
      if (!collectionUrl) {
        console.error("Missing dataset URL on element.");
        return null;
      }
  
      try {
        const response = await fetch(collectionUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch collection page: ${response.statusText}`);
        }
        const text = await response.text();
  
        const doc = new DOMParser().parseFromString(text, "text/html");
  
        const section = doc.querySelector(".wine-finder-section");
        if (!section) {
          console.warn("No section with class 'wine-finder' found.");
          return null;
        }
  
        const fullId = section.getAttribute("id");
        if (!fullId) {
          console.warn("Section found without an id attribute.");
          return null;
        }
  
        const templateId = fullId.replace(/^shopify-section-/, "");
          this.sectionID = templateId;
        return templateId;
      } catch (error) {
        console.error("Error in getTemplateId:", error);
        return null;
      }
    }

  buildFetchUrl(filters) {
      const rawUrl = this.getAttribute("data-url");
      const url = new URL(rawUrl, window.location.origin);
      const section = this.sectionID || this.getAttribute("data-section");

      if (!url || !section) {
          console.error("Missing data-url or data-section attribute.");
          return null;
      }

      const filterParams = new URLSearchParams();
      Object.keys(filters).forEach((key) => {
          if (filters[key]) {
              filterParams.append(key, filters[key]);
          }
      });

      console.log("[WineFinder] buildFetchUrl inputs:", { rawUrl, section, filters });

      return filterParams.toString()
          ? `${url.href}?${filterParams.toString()}&section_id=${section}`
          : `${url.href}?section_id=${section}`;
  }

  populateAllFilters(doc) {
      this.ensureAllFilterElementsExist(doc);

      this.querySelectorAll("wine-finder-options").forEach((optionElement) => {
          const filterKey = optionElement.getAttribute("data-filter");
          if (!filterKey) return;

          const fieldset = doc.querySelector(`fieldset[data-filter="${filterKey}"]`);
          if (!fieldset) {
              console.warn(`No matching fieldset found for filter: ${filterKey}`);
              return;
          }

          const fetchedOptions = this.extractOptions(fieldset);
          this.updateRadioOptions(optionElement, fetchedOptions, true);
      });
      this.reloadFilters();
      this.hasReloaded = true;
  }

  populateSubsequentFilters(doc, changedElement) {
      this.ensureAllFilterElementsExist(doc);

      const optionsElements = Array.from(this.querySelectorAll("wine-finder-options"));
      const changedIndex = optionsElements.indexOf(changedElement);

      optionsElements.forEach((optionElement, index) => {
          if (index <= changedIndex) return; // **Only update radio buttons after the changed one**

          const filterKey = optionElement.getAttribute("data-filter");
          if (!filterKey) return;

          const fieldset = doc.querySelector(`fieldset[data-filter="${filterKey}"]`);
          if (!fieldset) {
              // remove optionElement:
              optionElement.remove();
              console.warn(`No matching fieldset found for filter: ${filterKey}`);
              return;
          }

          const fetchedOptions = this.extractOptions(fieldset);
          this.updateRadioOptions(optionElement, fetchedOptions, false);

      });
  }

  extractOptions(fieldset) {
      return Array.from(fieldset.querySelectorAll("label")).map((label) => {
          const input = label.querySelector("input[type='radio']");
          return {
              value: input ? input.value : "",
              label: label.querySelector('.value-text').textContent.trim(),
              description: label.querySelector('.value-description')?.textContent.trim() || "",
              id: input ? input.id : "",
              selected: input ? input.hasAttribute("selected") : false
          };
      });
  }

  collectSelectedFilters(upToElement = null) {
      const filters = {};
      for (const optionElement of this.querySelectorAll("wine-finder-options")) {
          const selectedInput = optionElement.querySelector("input[type='radio']:checked");
          if (selectedInput) {
              filters[optionElement.getAttribute("data-filter")] = selectedInput.value;
          }
          if (optionElement === upToElement) break;
      }
      return filters;
  }


  updateRadioOptions(optionElement, fetchedOptions, isInitialLoad) {
      const details = optionElement.querySelector("details");
      const summary = details.querySelector(".wine-finder-title");
      const fieldset = details.querySelector("fieldset");

      if (!fieldset || !summary) {
          console.warn("Invalid structure inside wine-finder-options.");
          return;
      }

      // Capture the current selection (if any) before we clear out the fieldset.
      const previousValue = fieldset.querySelector("input:checked")?.value || "";

      // Clear previous radio options
      fieldset.innerHTML = "";

      // If no options are provided, clear the summary and exit.
      if (fetchedOptions.length === 0) {
          // Hide the whole option until it has content
          optionElement.setAttribute("hidden", "");
          summary.textContent = "";
          return;
      }

      // Ensure the option is visible when options are available
      optionElement.removeAttribute("hidden");

      let foundSelection = false;

      // Loop through the fetched options and build the new radio inputs.
      fetchedOptions.forEach((opt, index) => {
          const label = document.createElement("label");
          label.setAttribute("for", `${opt.id}`);

          const input = document.createElement("input");
          input.type = "radio";
          input.id = opt.id;
          input.name = optionElement.getAttribute("data-filter");
          input.value = opt.value;

          // For initial load: auto-select the first option if none is already selected.
          // For subsequent loads: only mark as checked if it matches the previous value.
          if (isInitialLoad) {
              if (opt.selected || (!previousValue && index === 0)) {
                //   input.checked = true;
                //   summary.textContent = opt.label;
                  foundSelection = true;
              }
          } else {
              if (previousValue && opt.value === previousValue) {
                  input.checked = true;
                //   summary.textContent = opt.label;
                  foundSelection = true;
              }
          }

          input.addEventListener("change", (event) => {
              this.changedByUser = true;
              // When the user manually changes the selection, update the summary immediately.
              summary.textContent = opt.label;
              const nextSibling = optionElement.nextElementSibling;

              console.log("[WineFinder] change event:", {
                  filterKey: optionElement.getAttribute("data-filter"),
                  name: event.target?.name,
                  value: event.target?.value,
                  checkedNow: !!event.target?.checked
              });

              // Defer to next frame so Safari updates :checked state before we collect filters
              requestAnimationFrame(() => {
                  const rechecked = optionElement.querySelector("input[type='radio']:checked")?.value;
                  console.log("[WineFinder] after rAF, checked value for",
                      optionElement.getAttribute("data-filter"),
                      "=",
                      rechecked
                  );
                  this.loadFilteredOptions(optionElement, nextSibling);
                  this.checkButtonState();
              });
          });

          // Create a wrapper div for the label content.
          const wrapperDiv = document.createElement("div");
          
          // Remove [advies = x] from the label text
          let cleanLabel = opt.label;
          cleanLabel = cleanLabel.replace(/\[advies = [^\]]+\]/, '').trim();
          
          wrapperDiv.innerHTML = `
              <div class="value-text">${cleanLabel}</div>
              <div class="value-description">${opt.description || ""}</div>
          `;

          // Append the input and its wrapper to the label, then add the label to the fieldset.
          label.appendChild(input);
          label.appendChild(wrapperDiv);
          fieldset.appendChild(label);
          if (isInitialLoad) {
              // find first input of each one and select it:
              const firstInput = fieldset.querySelector("input[type='radio']");
            //   firstInput.checked = true;
          }
      });

      // For subsequent loads, if the previous selection was not found, clear the summary.
      if (!isInitialLoad && !foundSelection) {
          // set summary to first selected option from optionElement:
          const selectedInput = optionElement.querySelector("input[type='radio']");
        //   selectedInput.checked = true;
        //   summary.textContent = selectedInput ? selectedInput.parentElement.querySelector('.value-text').textContent.trim() : "...";
          summary.textContent = "...";
      }

      // Check button state after updating options
      this.checkButtonState();
  }

  ensureAllFilterElementsExist(doc) {
      const existingFilters = Array.from(this.querySelectorAll("wine-finder-options")).map(el => el.getAttribute("data-filter"));
      const fetchedFieldsets = Array.from(doc.querySelectorAll("fieldset"));
  
      fetchedFieldsets.forEach(fieldset => {
          const filterKey = fieldset.getAttribute("data-filter");
  
          if (!existingFilters.includes(filterKey)) {
              this.createWineFinderOption(fieldset);
          }
      });

      // After ensuring all elements exist, hide any that are currently empty to avoid Safari :has/:empty quirks
      this.querySelectorAll("wine-finder-options").forEach((el) => {
          const hasOptions = el.querySelectorAll("fieldset label").length > 0;
          if (!hasOptions) el.setAttribute("hidden", "");
          else el.removeAttribute("hidden");
      });
  }
  
  createWineFinderOption(fieldset) {
      const filterKey = fieldset.getAttribute("data-filter");
      let legendText = fieldset.querySelector("legend")?.textContent.trim() || "Filter";
      
      // Extract the value after 'advies = ' from the options
      let adviesValue = "WIT"; // Default value
      const options = Array.from(fieldset.querySelectorAll("label"));
      for (const option of options) {
          const optionText = option.textContent.trim();
          if (optionText.includes("[advies = ")) {
              const match = optionText.match(/\[advies = ([^\]]+)\]/);
              if (match && match[1]) {
                  adviesValue = match[1];
                  break;
              }
          }
      }
      
      // Replace [suggestie] with the extracted value
      legendText = legendText.replace("[suggestie]", adviesValue);

      const template = this.querySelector("#wine-finder-template");
      if (!template) {
          console.error("Missing #wine-finder-template in the HTML.");
          return;
      }
  
      // Clone the template content
      const newOption = template.content.cloneNode(true).querySelector("wine-finder-options");
      newOption.setAttribute("data-filter", filterKey);

      // Find elements inside the template clone
      const span = newOption.querySelector(".wine-finder-options__item");
      const details = newOption.querySelector("details");
      const summary = details.querySelector("summary .wine-finder-title");
  
      // Ensure we replace `[opties]` and **only insert the disclosure once**
      let formattedLegend = legendText.replace("[opties]", details.outerHTML);
  
      // If `[opties]` was not present, append disclosure at the end
      if (!legendText.includes("[opties]")) {
          formattedLegend += " " + details.outerHTML;
      }
  
      // Set the legend text inside the <span>
      span.innerHTML = formattedLegend;

      // Append the new element inside the correct list
      this.querySelector(".wine-finder-list").appendChild(newOption);
  }

  updateProductGrid(doc) {
      const productGrid = doc.querySelector("#ProductGridContainer");
      if (!productGrid) {
          console.warn("No #ProductGridContainer found in the response.");
          return;
      }

      const existingGrid = this.closest('.shopify-section').querySelector("#ProductGridContainer");
      if (existingGrid) existingGrid.remove();

      this.closest('.shopify-section').appendChild(productGrid);
      productGrid.classList.remove('hidden');
      // scroll into view:
      productGrid.scrollIntoView({ behavior: "smooth" });
  }

  setupButton() {
      const button = this.querySelector("button[type='submit']");
      if (!button) return;

      button.addEventListener("click", (event) => {
          event.preventDefault();
          this.loadProducts();
      });
  }

  setupButtonStateCheck() {
      // Check button state initially
      this.checkButtonState();
      
      // Listen for changes on all radio inputs
      this.addEventListener("change", (event) => {
          if (event.target.type === "radio") {
              this.checkButtonState();
          }
      });
  }

  checkButtonState() {
      const button = this.querySelector("button[type='submit']");
      if (!button) return;

      // Get all wine-finder-options elements that are not hidden
      const optionElements = Array.from(this.querySelectorAll("wine-finder-options:not([hidden])"));
      
      // Check if all visible option elements have a selected input
      const allSelected = optionElements.every(optionElement => {
          const selectedInput = optionElement.querySelector("input[type='radio']:checked");
          return selectedInput !== null;
      });

      // Enable/disable button based on whether all fieldsets have selections
      button.disabled = !allSelected;
      
      // Handle title attribute
      if (allSelected) {
          // Remove title attribute when button is enabled
          button.removeAttribute('title');
      } else {
          // Add title attribute when button is disabled, using data-title if available
          const dataTitle = button.getAttribute('data-title');
          if (dataTitle) {
              button.setAttribute('title', dataTitle);
          }
      }
  }
}

customElements.define("wine-finder", WineFinder);

class WineFinderOptions extends HTMLElement {
  constructor() {
      super();
  }

  connectedCallback() {
      this.detailsElement = this.querySelector("details");
      if (!this.detailsElement) {
          console.warn("No <details> found inside wine-finder-options.");
          return;
      }

      this.setupAutoClose();
      this.detailsElement.addEventListener("toggle", (event) => {
          if (event.target.open === false) {
            //   this.closest('wine-finder').loadFilteredOptions(this);
            //   this.closest('wine-finder').loadFilteredOptions(this);
          }
      }
      );
  }

  setupAutoClose() {
      // Close <details> when selecting a radio button
      this.detailsElement.addEventListener("change", (event) => {
          if (event.target.type === "radio") {
              this.closeDetails();
          }
      });

      // Close <details> when clicking outside the element
      document.addEventListener("click", (event) => {
          if (!this.detailsElement.contains(event.target) && this.detailsElement.open) {
              this.closeDetails();

          }
      });
  }

  closeDetails() {
      this.detailsElement.removeAttribute("open");
  }
}

customElements.define("wine-finder-options", WineFinderOptions);